import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoWordmark } from "./Logo";
import { useLang, STR, pathForLang } from "../i18n";

export const Navbar = () => {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const t = STR[lang];
  const LINKS = [
    { label: t.nav.home, href: "#top" },
    { label: t.nav.studio, href: "#studio" },
    { label: t.nav.servizi, href: "#servizi" },
    { label: t.nav.progetti, href: "#progetti" },
    { label: t.nav.gallery, href: "#gallery" },
    { label: t.nav.contatti, href: "#contatti" },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  // Il selettore lingua e' un link all'URL dell'altra lingua (indicizzabile da Google)
  const LangSwitch = ({ className = "" }) => (
    <div className={`flex items-center gap-1 text-[11px] font-bold tracking-[0.08em] ${className}`} data-testid="lang-switch">
      {["it", "en"].map((l) => (
        <a
          key={l}
          href={pathForLang(pathname, l)}
          hrefLang={l}
          data-testid={`lang-${l}`}
          className={`px-2 py-1 uppercase transition-colors duration-300 ${
            lang === l ? "text-[#F18A00]" : "text-[#0D1B2A]/40 hover:text-[#0D1B2A]"
          }`}
        >
          {l}
        </a>
      ))}
    </div>
  );

  return (
    <>
      <motion.header
        initial={{ y: -104, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#edf0f2] transition-shadow duration-500 ${
          scrolled ? "shadow-[0_1px_20px_rgba(13,27,42,0.08)]" : ""
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1320px] mx-auto flex items-center justify-between gap-6 px-6 h-[104px]">
          <a href="#top" onClick={(e) => go(e, "#top")} data-testid="nav-logo-link">
            <LogoWordmark compact />
          </a>
          <nav className="hidden lg:flex items-center gap-[34px]" data-testid="nav-links">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-[13px] uppercase tracking-[0.045em] text-[#0D1B2A] border-b-2 border-transparent hover:border-[#F18A00] py-1.5 transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <LangSwitch />
            <a
              href="#contatti"
              onClick={(e) => go(e, "#contatti")}
              data-testid="nav-cta-button"
              className="inline-flex items-center gap-6 bg-[#0D1B2A] text-white px-[25px] py-[17px] text-xs uppercase tracking-[0.045em] hover:bg-[#F18A00] transition-colors duration-300"
            >
              {t.nav.cta} <span className="text-[21px] font-light leading-none">{"→"}</span>
            </a>
          </nav>
          <div className="lg:hidden flex items-center gap-2">
            <LangSwitch />
            <button
              className="text-[#0D1B2A]"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Apri menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white flex flex-col items-start justify-center px-10 gap-6"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
                data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                className="font-display text-4xl font-medium uppercase tracking-tight text-[#0D1B2A] hover:text-[#F18A00] transition-colors"
              >
                {l.label}
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
